<template>
  <aside class="fpp" :data-test="testId" aria-label="Preview">
    <div class="fpp-head">
      <span class="fpp-title">Preview</span>
      <div v-if="picks.length > 1" class="fpp-pick">
        <select
          class="fpp-select"
          :value="pick"
          aria-label="Which message"
          :data-test="testId + '-pick'"
          @change="$emit('update:pick', $event.target.value)"
        >
          <option v-for="p in picks" :key="p.key" :value="p.key">{{ p.label }}</option>
        </select>
      </div>
      <div class="fpp-pills" role="tablist" aria-label="Message type">
        <button
          type="button"
          role="tab"
          class="fpp-pill"
          :class="{ 'fpp-pill--on': channel === 'sms' }"
          :aria-selected="channel === 'sms' ? 'true' : 'false'"
          :data-test="testId + '-channel-sms'"
          @click="$emit('update:channel', 'sms')"
        >Text</button>
        <button
          type="button"
          role="tab"
          class="fpp-pill"
          :class="{ 'fpp-pill--on': channel === 'email' }"
          :aria-selected="channel === 'email' ? 'true' : 'false'"
          :data-test="testId + '-channel-email'"
          @click="$emit('update:channel', 'email')"
        >Email</button>
      </div>
    </div>

    <div class="fpp-sample" :data-test="testId + '-sample'">Sample patient: Maria Lopez &middot; {{ aptWord }} {{ sample.label }}</div>
    <div v-if="off" class="fpp-off" role="status" :data-test="testId + '-off'">
      <i class="fa fa-info-circle" aria-hidden="true"></i>{{ offText }}
    </div>

    <div class="fpp-stage" :class="{ 'fpp-stage--off': off }">
      <message-preview
        :channel="channel === 'sms' ? 'text' : 'email'"
        :sms-body="message.body"
        :email="{ subject: message.subject || '', body: emailBody }"
        :site="site"
        :vars="extraVars"
        :form-link-title="linkTitle"
      />
    </div>
  </aside>
</template>

<script>
import MessagePreview from './MessagePreview.vue';
import { describeLinkChoice, previewExtraVars } from '@/utils/formsSettings/editorModel';

/**
 * The right-hand preview of the two sending tabs (board FormsSettings.dc.html "Preview"): a Text | Email switch, in rules mode a picker for WHICH
 * message ("Everyone else" / each rule), the sample patient line, the "this message won't send" note, and the post-op tab's own MessagePreview
 * (phone bubble / email card with the site's real name, phone, sender and signature) — reused through its `vars` / `form-link-title` props, never forked.
 *
 * Presentational: the tab hands it the message to draw (`message` = { on, body, subject?, formLink }, `on` a Boolean) and the sample appointment; it
 * emits `update:channel` / `update:pick`. {{form_link}} is drawn as the masked link in the text bubble and, as a paragraph of its own in an email, as a
 * card with the picked form / packet's name ("— N forms" for a packet; "No form picked yet" until one is).
 */
export default {
  name: 'PfeFormsPreviewPane',
  components: { MessagePreview },
  props: {
    channel: { type: String, default: 'sms', validator: (v) => v === 'sms' || v === 'email' },
    message: { type: Object, required: true },
    site: { type: Object, default: () => ({}) },
    forms: { type: Array, default: () => [] },
    packets: { type: Array, default: () => [] },
    /** The sample appointment (editorModel sampleAppointment). */
    sample: { type: Object, required: true },
    /** 'first visit' (New Patients) / 'appointment' (Medical History). */
    aptWord: { type: String, default: 'appointment' },
    /** Rules mode: [{ key, label }] — "Everyone else" first. Fewer than two ⇒ no picker. */
    picks: { type: Array, default: () => [] },
    pick: { type: String, default: '' },
    testId: { type: String, default: 'pfe-fpreview' },
  },
  computed: {
    off() { return this.message.on === false; },
    offText() { return `${this.channel === 'sms' ? 'Text' : 'Email'} is off — this message won’t send.`; },
    // The layout appends the practice's signature to every email (nobody types it): the preview draws it — or the "signature is off" note — last.
    emailBody() { return `${this.message.body || ''}\n\n{{email_signature_block}}`; },
    extraVars() { return previewExtraVars(this.sample); },
    linkTitle() { return describeLinkChoice(this.message.formLink, this.forms, this.packets).title; },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.fpp {
  flex: 1 1 420px;
  max-width: 600px;
  min-width: 0;
  position: sticky;
  top: 0;
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.fpp-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fpp-title { flex: 1; font-family: $pp-font-display; font-weight: 800; font-size: 18px; letter-spacing: -0.01em; }
.fpp-pick { min-width: 0; }
.fpp-select {
  @include pfe-field(36px);
  height: 36px;
  max-width: 220px;
  padding: 0 10px;

  &:focus { @include pfe-focus-ring; }
}
.fpp-pills { display: inline-flex; padding: 3px; border-radius: 999px; background: $pfe-neutral-100; gap: 2px; }
.fpp-pill {
  height: 30px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: $pfe-neutral-600;
  cursor: pointer;

  &--on { background: $pp-surface; color: $pp-text; box-shadow: $pp-shadow-raised; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }
}
.fpp-sample { margin-top: -6px; font-size: 13px; color: $pfe-neutral-500; }
.fpp-off {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: $pfe-neutral-50;
  font-size: 13px;
  color: $pfe-neutral-600;

  i { color: $pfe-neutral-400; }
}
.fpp-stage { transition: opacity 0.15s ease; &--off { opacity: 0.55; } }

// Narrow screens: the preview stacks under the editor instead of sitting beside it (the post-op tab does the same).
@media (max-width: 1100px) {
  .fpp { position: static; max-width: none; flex-basis: 100%; }
}
</style>
